import React from 'react'
import styled from 'styled-components'
import {Calendar} from 'lucide-react'
import {Tooltip} from '../BaseComponents'
import {Spinner} from '../Primitives'
import {CardKebabSlot, DonutCard, type DonutTone} from './DonutCard'
import {CardTextLine} from './CardTextLine'
import {OrderHeaderFlag} from './OrderHeaderFlag'
import {EllipsisText} from './Table'

/**
 * ServiceOrderCard — extended (v2.1)
 *
 * Full DASHBOARD-GUIDE anatomy. The card composes:
 *
 *   - Cyan top half with radial-gradient overlay (component identity,
 *     not single-instance UI accent — see DS color rule)
 *   - White body with: StatusPill -> status message -> optional pipeline
 *   - Footer CTA slot with primary / outline / ghost hierarchy
 *
 * Used in both MSP and Company dashboard views:
 *   - MSP view: omits `pipeline` prop — no stepper shown
 *   - Company view: passes `pipeline` with HorizontalStepper config
 *
 * The component is dumb. All content — labels, IDs, dates, stats,
 * status, message, button — is passed in. Business logic lives in the app.
 *
 * @example MSP pending card (no pipeline)
 *   <ServiceOrderCard
 *     id="SO-345678"
 *     date="Oct 06"
 *     jobRole="Packer, Shift 2"
 *     shift="Created 10/03/2023"
 *     meta={[
 *       { label: 'Client', value: 'Forever21' },
 *       { label: 'Manager', value: 'John Doe' },
 *     ]}
 *     status={{ tone: 'warning', label: 'New order' }}
 *     message="New request received. Start preparing pledges."
 *     primaryAction={{ label: 'Prepare pledges', onClick: () => {}, variant: 'primary' }}
 *   />
 *
 * @example Company card (with pipeline)
 *   <ServiceOrderCard
 *     id="SO-3333"
 *     date="Oct 06"
 *     jobRole="Forklift"
 *     shift="Shift 1 · 06:00-14:00"
 *     meta={[{ label: 'Manager', value: 'John Doe' }]}
 *     stats={[
 *       { label: 'Fill ratio', value: '3 / 12' },
 *       { label: 'Fill rate', value: '25%' },
 *     ]}
 *     status={{ tone: 'pending', label: 'Confirming' }}
 *     message="The MSP is working on your order."
 *     pipeline={{
 *       steps: [
 *         { icon: <i className="ti ti-puzzle" />, label: 'Submit' },
 *         { icon: <i className="ti ti-clipboard" />, label: 'Process' },
 *         { icon: <i className="ti ti-send" />, label: 'Confirm' },
 *         { icon: <i className="ti ti-list-check" />, label: 'Fill' },
 *       ],
 *       currentStep: 3,
 *     }}
 *     primaryAction={{ label: 'View order', onClick: () => {}, variant: 'outline' }}
 *   />
 */

export type StatusPillTone =
  | 'neutral'
  | 'pending'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'outline'

export type ServiceOrderCardButtonVariant = 'primary' | 'outline' | 'ghost'

/** Status tone for the optional body notice pill (maps onto the DS status scale). */
export type ServiceOrderNoticeTone =
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'pending'
  | 'neutral'

export interface ServiceOrderMeta {
  label: string
  value: React.ReactNode
}

export interface ServiceOrderStat {
  label: string
  value: React.ReactNode
  /** Render value at 60% opacity (for "no data" — em dash etc.) */
  empty?: boolean
}

export interface PipelineStep {
  /** Tabler icon node or any ReactNode */
  icon: React.ReactNode
  /** Optional accessible label per step */
  label?: string
}

export interface ServiceOrderCardProps {
  /** Order identifier (rendered as a mono chip in the cyan header) */
  id: string
  /** Date shown top-right of the cyan header. Accepts a string or ReactNode (e.g. a dropdown for multi-day orders). */
  date: React.ReactNode
  /** Role / position title (e.g. "Forklift"). Named jobRole to avoid collision with the HTML role attribute. */
  jobRole: string
  /** Subtitle below the role (shift, schedule, or created date) */
  shift?: string
  /** Manager / client / source lines */
  meta?: ServiceOrderMeta[]
  /** Stats row at the bottom of the cyan header (e.g. fill ratio + fill rate) */
  stats?: ServiceOrderStat[]

  /** Show a small backup-shield indicator beside the id chip (order has backup orders). */
  hasBackups?: boolean
  /**
   * Opt-in: what the backup shield does. Set, the shield is a button (it opens the
   * backup order) that never opens the card. Unset, it is a plain icon and a click opens the card.
   */
  onBackupClick?: () => void
  /** The shield button's accessible name, e.g. "Open backup order JO-429". Default "Open backup order". */
  backupLabel?: string
  /** The viewer can't open the backup order: the shield keeps its tooltip, aria-disabled, no action. */
  backupDisabled?: boolean

  /** Status pill in the white body. Omit to hide the status section (e.g. combo mode). */
  status?: {tone: StatusPillTone; label: string}
  /** Plain-text message below the status pill */
  message?: React.ReactNode

  /** Optional notice pill at the top of the white body (e.g. urgency / outcome). */
  notice?: {tone: ServiceOrderNoticeTone; label: string}
  /** Optional left-accent frame color (urgency emphasis). */
  accent?: 'error' | 'warning'
  /** Optional custom row rendered as the last child of the cyan header (e.g. day-nav). */
  scheduleRow?: React.ReactNode
  /** Optional fill ring used by settled/closed service-order cards. */
  progress?: {value: number; max: number; tone?: DonutTone}

  /** Pipeline / stepper config (omit for MSP view, include for company view) */
  pipeline?: {
    steps: PipelineStep[]
    /** 1-indexed current step (>= 1, <= steps.length) */
    currentStep: number
  }

  /** Primary action in the footer */
  primaryAction?: {
    label: string
    onClick?: () => void
    variant?: ServiceOrderCardButtonVariant
    iconLeft?: React.ReactNode
    /** Opt-in: the button cannot be pressed (e.g. while another action on the card saves). */
    disabled?: boolean
    /** Opt-in: the action is saving. A spinner replaces `iconLeft`, and the button is disabled and `aria-busy`. */
    loading?: boolean
    /** Opt-in: `data-testid` on the button. */
    testId?: string
  }
  /** Secondary action (small icon buttons next to the primary CTA) */
  secondaryActions?: Array<{
    icon: React.ReactNode
    onClick?: () => void
    ariaLabel: string
    /** Opt-in: the button cannot be pressed. */
    disabled?: boolean
    /** Opt-in: `data-testid` on the button. */
    testId?: string
  }>
  /** Node rendered in the header top-right, right of the date (e.g. a kebab menu). */
  headerAction?: React.ReactNode

  /** Click anywhere on the card (excluding actions) */
  onClick?: () => void
  /** Hover-lift + border-cyan when this card is "selected" by parent */
  selected?: boolean

  /** Optional className for styled overrides (combo border-radius etc.) */
  className?: string
  /** Visual variant: 'standard' (cyan top) or 'flat' (white, neutral text). Default standard. */
  variant?: 'standard' | 'flat'
  /**
   * Opt-in emphasis (STFFLG-4184). `outlined` draws a `--brand-cta` border all round, so a card that
   * asks for an answer (a job order invitation on Pending Orders) stands out among the order cards.
   * Unset, the card renders exactly as before.
   */
  emphasis?: 'outlined'
  /** Opt-in: `data-testid` on the card's root. */
  'data-testid'?: string
}

/* ====================================================================
   STATUS PILL TONES
   ==================================================================== */
const PILL_TONES: Record<StatusPillTone, {bg: string; fg: string; dot: string; border?: string}> = {
  neutral: {
    bg: 'var(--neutral-pill-bg, var(--neutral-200))',
    fg: 'var(--primary-800)',
    dot: 'var(--primary-700)',
  },
  pending: {bg: 'var(--pending-100)', fg: 'var(--pending-800)', dot: 'var(--pending-500)'},
  info: {bg: 'var(--info-100)', fg: 'var(--info-800)', dot: 'var(--info-500)'},
  success: {bg: 'var(--success-100)', fg: 'var(--success-800)', dot: 'var(--success-500)'},
  warning: {bg: 'var(--warning-100)', fg: 'var(--warning-800)', dot: 'var(--warning-500)'},
  error: {bg: 'var(--error-100)', fg: 'var(--error-800)', dot: 'var(--error-500)'},
  outline: {
    bg: 'var(--bg-card)',
    fg: 'var(--primary-800)',
    dot: 'var(--primary-700)',
    border: 'var(--primary-700)',
  },
}

const NOTICE_TONES: Record<ServiceOrderNoticeTone, {bg: string; fg: string; dot: string}> = {
  error: {bg: 'var(--error-100)', fg: 'var(--error-800)', dot: 'var(--error-500)'},
  warning: {bg: 'var(--warning-100)', fg: 'var(--warning-800)', dot: 'var(--warning-500)'},
  info: {bg: 'var(--info-100)', fg: 'var(--info-800)', dot: 'var(--info-500)'},
  success: {bg: 'var(--success-100)', fg: 'var(--success-800)', dot: 'var(--success-500)'},
  pending: {bg: 'var(--pending-100)', fg: 'var(--pending-800)', dot: 'var(--pending-500)'},
  neutral: {bg: 'var(--neutral-100)', fg: 'var(--primary-800)', dot: 'var(--primary-700)'},
}

const ACCENT_COLOR: Record<'error' | 'warning', string> = {
  error: 'var(--error-500)',
  warning: 'var(--warning-500)',
}

/* ====================================================================
   STYLED PRIMITIVES
   ==================================================================== */
const Card = styled.article<{
  $selected: boolean
  $clickable: boolean
  $variant: 'standard' | 'flat'
  $accent?: 'error' | 'warning'
  $outlined: boolean
}>`
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-strong);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.15s;
  cursor: ${({$clickable}) => ($clickable ? 'pointer' : 'default')};
  border: var(--border-thin) solid
    ${({$selected, $outlined}) => ($selected || $outlined ? 'var(--brand-cta)' : 'transparent')};
  ${({$accent}) => $accent && `border-left: 3px solid ${ACCENT_COLOR[$accent]};`}
  height: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-elevated);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

const Top = styled.div<{$variant: 'standard' | 'flat'}>`
  background: ${({$variant}) =>
    $variant === 'flat'
      ? 'var(--bg-card)'
      : 'linear-gradient(135deg, var(--brand-500) 0%, var(--brand-700) 100%)'};
  color: ${({$variant}) => ($variant === 'flat' ? 'var(--primary-800)' : 'var(--text-on-brand)')};
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  position: relative;

  &::after {
    content: ${({$variant}) => ($variant === 'flat' ? 'none' : "''")};
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 110% -20%, rgba(255, 255, 255, 0.18), transparent 60%);
    pointer-events: none;
  }
`

const IdRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--space-8);
  position: relative;
`

/** Right side of the header row: date + optional header action (e.g. a kebab menu). */
const HeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;
`

const IdChip = styled.span<{$variant: 'standard' | 'flat'}>`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  font-weight: 600;
  background: ${({$variant}) =>
    $variant === 'flat' ? 'var(--neutral-100)' : 'var(--bg-elevated)'};
  color: var(--primary-800);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
`

/** Groups the id chip with the optional backup shield so they stay together
    on the left while the date stays right. */
const IdLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
`

const DateText = styled.span<{$variant: 'standard' | 'flat'}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: 0.75rem;
  color: ${({$variant}) =>
    $variant === 'flat' ? 'var(--text-secondary)' : 'var(--text-on-brand)'};
  font-weight: 500;
  opacity: 0.92;
  font-family: var(--font-sans);
  white-space: nowrap;

  > i,
  > svg {
    font-size: 14px;
  }
`

const Headline = styled.div`
  position: relative;
`

/* Role and shift: the EllipsisText inside each cuts a long value with "…", and only then does its
   tooltip show the whole value (STFFLG-4236). */
const RoleText = styled.div<{$variant: 'standard' | 'flat'}>`
  font-family: var(--font-sans);
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({$variant}) => ($variant === 'flat' ? 'var(--primary-800)' : 'var(--text-on-brand)')};
  letter-spacing: -0.02em;
  line-height: 1.15;
  overflow: hidden;
`

const ShiftText = styled.div<{$variant: 'standard' | 'flat'}>`
  font-size: 0.875rem;
  color: ${({$variant}) => ($variant === 'flat' ? 'var(--neutral-700)' : 'var(--text-on-brand)')};
  font-weight: 500;
  opacity: 0.92;
  margin-top: 2px;
  overflow: hidden;
`

const MetaBlock = styled.div<{$variant: 'standard' | 'flat'}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.75rem;
  color: ${({$variant}) =>
    $variant === 'flat' ? 'var(--text-secondary)' : 'var(--text-on-brand)'};
  opacity: 0.85;
  overflow: hidden;

  > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  strong {
    color: ${({$variant}) => ($variant === 'flat' ? 'var(--primary-800)' : 'var(--text-on-brand)')};
    font-weight: 600;
    opacity: 1;
  }
`

const StatsBand = styled.div<{$cols: number; $variant: 'standard' | 'flat'}>`
  display: grid;
  grid-template-columns: repeat(${({$cols}) => $cols}, 1fr);
  gap: var(--space-3);
  padding: var(--space-3) 0 0;
  border-top: 1px solid
    ${({$variant}) => ($variant === 'flat' ? 'var(--divider)' : 'rgba(255, 255, 255, 0.18)')};
  position: relative;
`

const StatBlock = styled.div``

const StatLabel = styled.div<{$variant: 'standard' | 'flat'}>`
  font-size: 0.625rem;
  font-weight: 700;
  color: ${({$variant}) =>
    $variant === 'flat' ? 'var(--text-secondary)' : 'var(--text-on-brand)'};
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 2px;
`

const StatValue = styled.div<{$empty: boolean; $variant: 'standard' | 'flat'}>`
  font-family: var(--font-mono);
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({$variant}) => ($variant === 'flat' ? 'var(--primary-800)' : 'var(--text-on-brand)')};
  letter-spacing: -0.01em;
  opacity: ${({$empty}) => ($empty ? 0.6 : 1)};
`

/* -------- White body -------- */

const Body = styled.div`
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  flex: 1;
`

const Pill = styled.span<{$tone: StatusPillTone}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: fit-content;
  background: ${({$tone}) => PILL_TONES[$tone].bg};
  color: ${({$tone}) => PILL_TONES[$tone].fg};
  border: ${({$tone}) =>
    PILL_TONES[$tone].border
      ? `1.5px solid ${PILL_TONES[$tone].border}`
      : '1.5px solid transparent'};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({$tone}) => PILL_TONES[$tone].dot};
    flex-shrink: 0;
  }
`

const Message = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-700);
  line-height: 1.5;

  strong {
    color: var(--primary-800);
    font-weight: 600;
  }
`

const NoticePill = styled.div<{$tone: ServiceOrderNoticeTone}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  align-self: flex-start;
  max-width: 100%;
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 700;
  background: ${({$tone}) => NOTICE_TONES[$tone].bg};
  color: ${({$tone}) => NOTICE_TONES[$tone].fg};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({$tone}) => NOTICE_TONES[$tone].dot};
  }
`

const ProgressRow = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--space-2) 0;
`

/* -------- Pipeline -------- */

const PipelineWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: var(--space-1) 0;
`

const Step = styled.span<{$state: 'done' | 'current' | 'todo'}>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s;

  ${({$state}) => {
    if ($state === 'done') return `background: var(--brand); color: var(--text-on-brand);`
    if ($state === 'current')
      return `
        background: var(--brand);
        color: var(--text-on-brand);
        box-shadow: 0 0 0 4px var(--brand-100), 0 2px 6px rgba(3,169,252,0.35);
      `
    return `
      background: var(--bg-card);
      color: var(--text-disabled);
      border: 1.5px solid var(--neutral-300);
    `
  }}
`

const StepLine = styled.span<{$done: boolean}>`
  flex: 1;
  height: 2px;
  background: ${({$done}) => ($done ? 'var(--brand)' : 'var(--divider)')};
  min-width: 6px;
`

/* -------- Footer -------- */

/**
 * Footer container for primary/secondary action buttons.
 * When `$onCyan` is true (body is empty → footer sits directly under
 * the cyan Top), the footer gets a matching cyan background so the
 * card reads as one seamless cyan block. Outline-variant buttons
 * naturally contrast (white fill on cyan).
 */
const Foot = styled.div<{$onCyan?: boolean}>`
  padding: ${({$onCyan}) =>
    $onCyan ? 'var(--space-3) var(--space-4) var(--space-5)' : '0 var(--space-4) var(--space-5)'};
  display: flex;
  gap: var(--space-2);
  ${({$onCyan}) =>
    $onCyan &&
    `
    background: var(--brand);
    position: relative;
  `}
`

const PrimaryBtn = styled.button<{$variant: ServiceOrderCardButtonVariant}>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: 0 var(--space-4);
  height: 42px;
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: background 0.15s, box-shadow 0.15s, border-color 0.15s, color 0.15s;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${({$variant}) => {
    if ($variant === 'primary')
      return `
        background: var(--brand-cta);
        color: var(--text-on-brand);
        box-shadow: 0 1px 2px rgba(3,169,252,0.3), 0 2px 6px rgba(3,169,252,0.2);
        &:hover { background: var(--brand-hover); }
      `
    if ($variant === 'outline')
      return `
        background: var(--bg-card);
        color: var(--brand-active);
        border: 1.5px solid var(--brand-cta);
        &:hover { background: var(--brand-50); }
      `
    return `
      background: var(--neutral-100);
      color: var(--primary-800);
      &:hover { background: var(--neutral-200); }
    `
  }}
`

const SecondaryBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-active);
  background: var(--brand-50);
  border: 0;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.15s;

  &:hover {
    background: var(--brand-100);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

/* ====================================================================
   COMPONENT
   ==================================================================== */
export const ServiceOrderCard: React.FC<ServiceOrderCardProps> = ({
  id,
  date,
  jobRole,
  shift,
  meta,
  stats,
  status,
  message,
  notice,
  accent,
  scheduleRow,
  progress,
  pipeline,
  primaryAction,
  secondaryActions,
  headerAction,
  onClick,
  selected = false,
  className,
  variant = 'standard',
  hasBackups = false,
  onBackupClick,
  backupLabel,
  backupDisabled = false,
  emphasis,
  'data-testid': testId,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card-level click when interacting with buttons inside
    if ((e.target as HTMLElement).closest('button')) return
    onClick?.()
  }

  // When the white body section has no content (no status, message, or
  // pipeline), the footer sits directly under the cyan Top. We render
  // the footer with a matching cyan background so the card reads as
  // one seamless block — common in combo-mode where status/message are
  // omitted because the paired DonutCard below shows that info.
  const hasBody = Boolean(
    status || message || notice || progress || (pipeline && pipeline.steps.length > 0),
  )
  const hasFooter = Boolean(primaryAction || (secondaryActions && secondaryActions.length > 0))
  const footerOnCyan = !hasBody && hasFooter && variant === 'standard'

  return (
    <Card
      className={className}
      $selected={selected}
      $clickable={Boolean(onClick)}
      $variant={variant}
      $accent={accent}
      $outlined={emphasis === 'outlined'}
      data-testid={testId}
      onClick={onClick ? handleCardClick : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-labelledby={`so-${id}-role`}>
      <Top $variant={variant}>
        <IdRow>
          <IdLeft>
            <IdChip $variant={variant}>{id}</IdChip>
            {hasBackups && (
              <OrderHeaderFlag
                kind="backup"
                surface={variant === 'flat' ? 'card' : 'onBrand'}
                label={onBackupClick ? backupLabel ?? 'Open backup order' : 'Has backup orders'}
                tooltip="Has backup orders"
                onActivate={onBackupClick}
                disabled={backupDisabled}
                data-testid="backup-shield-badge"
              />
            )}
          </IdLeft>
          <HeaderRight>
            <DateText $variant={variant}>
              <Calendar size={14} aria-hidden="true" />
              {date}
            </DateText>
            {headerAction && <CardKebabSlot>{headerAction}</CardKebabSlot>}
          </HeaderRight>
        </IdRow>

        <Headline>
          <RoleText $variant={variant} id={`so-${id}-role`}>
            <Tooltip content={jobRole} block>
              <EllipsisText>{jobRole}</EllipsisText>
            </Tooltip>
          </RoleText>
          {shift && (
            <ShiftText $variant={variant}>
              <CardTextLine>{shift}</CardTextLine>
            </ShiftText>
          )}
        </Headline>

        {meta && meta.length > 0 && (
          <MetaBlock $variant={variant}>
            {meta.map((m, i) => (
              <div key={`${m.label}-${i}`}>
                <CardTextLine>
                  <strong>{m.label}:</strong> {m.value}
                </CardTextLine>
              </div>
            ))}
          </MetaBlock>
        )}

        {stats && stats.length > 0 && (
          <StatsBand $cols={stats.length} $variant={variant}>
            {stats.map((s, i) => (
              <StatBlock key={`${s.label}-${i}`}>
                <StatLabel $variant={variant}>{s.label}</StatLabel>
                <StatValue $empty={Boolean(s.empty)} $variant={variant}>
                  {s.value}
                </StatValue>
              </StatBlock>
            ))}
          </StatsBand>
        )}

        {scheduleRow}
      </Top>

      {/* Body: only rendered when there's a notice, status, pipeline, or message.
          In combo mode these are omitted so the card is just cyan Top + Footer. */}
      {hasBody && (
        <Body>
          {notice && <NoticePill $tone={notice.tone}>{notice.label}</NoticePill>}
          {status && <Pill $tone={status.tone}>{status.label}</Pill>}

          {pipeline && pipeline.steps.length > 0 && (
            <PipelineWrap
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={pipeline.steps.length}
              aria-valuenow={pipeline.currentStep}
              aria-label={`Step ${pipeline.currentStep} of ${pipeline.steps.length}`}>
              {pipeline.steps.map((step, i) => {
                const stepNum = i + 1
                const isDone = stepNum < pipeline.currentStep
                const isCurrent = stepNum === pipeline.currentStep
                const state = isDone ? 'done' : isCurrent ? 'current' : 'todo'
                return (
                  <React.Fragment key={i}>
                    <Step $state={state} aria-label={step.label}>
                      {step.icon}
                    </Step>
                    {i < pipeline.steps.length - 1 && (
                      <StepLine $done={isDone} aria-hidden="true" />
                    )}
                  </React.Fragment>
                )
              })}
            </PipelineWrap>
          )}

          {message && <Message>{message}</Message>}
          {progress && (
            <ProgressRow>
              <DonutCard
                variant="bare"
                size="sm"
                value={progress.value}
                max={progress.max}
                tone={progress.tone}
                centerLabel="Filled"
              />
            </ProgressRow>
          )}
        </Body>
      )}

      {hasFooter && (
        <Foot $onCyan={footerOnCyan}>
          {secondaryActions?.map((action, i) => (
            <SecondaryBtn
              key={i}
              type="button"
              aria-label={action.ariaLabel}
              disabled={action.disabled}
              data-testid={action.testId}
              onClick={(e) => {
                e.stopPropagation()
                action.onClick?.()
              }}>
              {action.icon}
            </SecondaryBtn>
          ))}
          {primaryAction && (
            <PrimaryBtn
              type="button"
              $variant={primaryAction.variant ?? 'primary'}
              disabled={primaryAction.disabled || primaryAction.loading}
              aria-busy={primaryAction.loading || undefined}
              data-testid={primaryAction.testId}
              onClick={(e) => {
                e.stopPropagation()
                primaryAction.onClick?.()
              }}>
              {primaryAction.loading ? <Spinner aria-hidden="true" /> : primaryAction.iconLeft}
              {primaryAction.label}
            </PrimaryBtn>
          )}
        </Foot>
      )}
    </Card>
  )
}

ServiceOrderCard.displayName = 'ServiceOrderCard'
